import { FC, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";

import { formatDate } from "capitalrx-utilities";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Heading, Title } from "capitalrx-components-2.0/lib/typography";
import { Spinner } from "capitalrx-components-2.0/lib/general";

import {
  CaseTestClaimForm,
  CaseQuestionnaireForm,
  PACaseCreateForm,
  PAQuestionnaireForm,
  PACreateErrorDisplay,
  BackLink,
} from "@/components";
import { createPACaseAPI, createPACaseDraftAPI, getStatePatient } from "@/api";
import { Auth } from "@/api/interfaces";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeURL, resolvePACaseCreateError, stripPhoneFormatting } from "@/utils";

import styles from "./PACreatePage.module.scss";
import CaseDraftForm from "@/components/PACreateForm/CaseDraftForm";


enum FormStates {
  TestClaim = 1,
  Questionnaire = 2,
  SendDraft = 3,
}

// Also the renew page.
export const PACreatePage: FC = () => {
  const navigate = useNavigate();
  const showAlert = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const [backUrl, setBackUrl] = useState<string>(getThemeURL("HOME"));
  const [loading, setLoading] = useState<boolean>(false);

  const [isRenewal, setIsRenewal] = useState<boolean>(false);
  const [initialRenewalNpi, setInitialRenewalNpi] = useState<string>("");
  const [initialRenewalCode, setInitialRenewalCode] = useState<string>("");
  const [previousCaseId, setPreviousCaseId] = useState<number | null>(null);
  const [backToEditTrigger, setBackToEditTrigger] = useState<number>(0);

  const [prefillQuestions, setPrefillQuestions] = useState<Auth.QuestionAnswers>();
  const [createError, setCreateError] = useState<{
    message: string;
    errorCode?: string;
    details?: Record<string, string>;
  } | null>(null);

  const form = useForm<PACaseCreateForm>({
    defaultValues: {
      dispensing_site: "pharmacy",
    },
  });

  const [showPrescriberFaxField, setShowPrescriberFaxField] = useState<boolean>(false);
  const questionnaireForm = useForm<PAQuestionnaireForm>();

  const alternateFax = form.watch("alternate_fax_number");


  const prefillFromState = useCallback(async (oauthState: string) => {
    setLoading(true);
    try {
      const statePatient = await getStatePatient({ oauth_state: oauthState });

      if (statePatient.date_of_birth) {
        form.setValue("member_date_of_birth", formatDate(statePatient.date_of_birth, "MM/DD/YYYY"));
      }
      if (statePatient.external_member_id) {
        form.setValue("external_member_id", statePatient.external_member_id);
      }
      if (statePatient.first_name) {
        form.setValue("member_first_name", statePatient.first_name);
      }
      if (statePatient.last_name) {
        form.setValue("member_last_name", statePatient.last_name);
      }
      if (statePatient.provider_npi) {
        form.setValue("npi", statePatient.provider_npi);
      }
      if (statePatient.diagnosis_codes) {
        form.setValue("icd_10", statePatient.diagnosis_codes);
      }
      if (statePatient.question_answers) {
        setPrefillQuestions(statePatient.question_answers);
      }
    } catch (error) {
      console.error("Failed to get patient state", error);
    }
    setLoading(false);
  }, [form]);

  useEffect(() => {
    const oauthState = searchParams.get("oauth_state");
    const renewalNpi = searchParams.get("prescriber_npi");
    const renewalCode = searchParams.get("renewal_code");
    const requestedBackUrl = searchParams.get("back_url");

    if (requestedBackUrl) {
      setBackUrl(requestedBackUrl);
    }

    if (oauthState) {
      prefillFromState(oauthState);
    } else if (renewalNpi && renewalCode) {
      setIsRenewal(true);
      setInitialRenewalNpi(renewalNpi);
      setInitialRenewalCode(renewalCode);
      form.setValue("case_type", "renewal");
      logEvent(ANALYTIC_EVENTS.PA_CASE_RENEW.new);
    }
    setSearchParams({});
  }, [searchParams, prefillFromState, setSearchParams]);

  useEffect(() => {
    const hasAlternate = !!alternateFax?.toString().trim();
    setShowPrescriberFaxField(!hasAlternate);
  }, [alternateFax]);

  const getNextStep = () => {
    const formValues = form.getValues();
    if (!formValues?.test_claim || formValues.review_type === "no_review_needed") {
      return FormStates.TestClaim;
    } else {
      return FormStates.Questionnaire;
    }
  };

  const [formProgress, setFormProgress] = useState<FormStates>(getNextStep());

  const goToNextStep = (targetStep?: FormStates) => {
    let nextStep = getNextStep();

    if (targetStep) {
      nextStep = targetStep;
    }
    setFormProgress(nextStep);
  };
  const goBackToEdit = useCallback(() => {
    setFormProgress(FormStates.TestClaim);
    setBackToEditTrigger((prev) => prev + 1);
  }, []);
  const submitPriorAuthCase = useCallback(async () => {
    setCreateError(null);
    logEvent(isRenewal ? ANALYTIC_EVENTS.PA_CASE_RENEW.submit : ANALYTIC_EVENTS.PA_CASE_CREATE.submit);

    const formVals = form.getValues();

    const sanitizedData = {
      client_id: formVals.test_claim.client_id,
      external_member_id: formVals.test_claim.external_member_id,
      member_first_name: formVals.member_first_name,
      member_last_name: formVals.member_last_name,
      member_date_of_birth: formVals.member_date_of_birth,
      person_code: formVals.test_claim.person_code,
      is_urgent: !!formVals.is_urgent,
      review_type: formVals.review_type,
      drug_ndc: formVals.ndc,
      drug_quantity: formVals.quantity_dispensed,
      drug_days_supply: formVals.days_supply,
      drug_name: formVals.test_claim.drug_name,
      drug_directions_for_use: formVals.directions_for_use,
      icd_10: formVals.icd_10,
      provider_npi: formVals.npi,

      provider_phone: formVals.provider_phone,
      provider_fax: formVals.provider_fax,
      provider_first_name: formVals.provider_first_name,
      provider_last_name: formVals.provider_last_name,

      provider_email: formVals.provider_email,
      provider_address: formVals.provider_address,
      provider_address_2: formVals.provider_address_2,
      provider_city: formVals.provider_city,
      provider_state: formVals.provider_state,
      provider_zip: formVals.provider_zip,

      alternate_fax_number: stripPhoneFormatting(formVals.alternate_fax_number),
      dispensing_site: formVals.dispensing_site,
      facility_npi: formVals.facility_npi,
      questionnaire_responses: formVals.questionnaire_responses,
      daw_code: formVals.daw_code,
      renewal_code: isRenewal ? formVals.renewal_code : undefined,
    };

    try {
      const { case_id: caseId } = await createPACaseAPI(sanitizedData);
      goToCase(caseId);

    } catch (error) {
      const resolution = await resolvePACaseCreateError(error);
      if (resolution.caseId !== null) {
        goToCase(resolution.caseId);
        return;
      }

      setCreateError(resolution.error);
      showAlert({ type: "error", style: "banner", message: resolution.error.message });

      logEvent(ANALYTIC_EVENTS.ERRORS[resolution.error.analyticsEvent] || ANALYTIC_EVENTS.ERRORS.caseCreate);

      window.scrollTo({ top: 0, behavior: "smooth" });
    }

  }, [showAlert, form, isRenewal]);

  const submitPriorAuthCaseDraft = useCallback(async () => {
    setCreateError(null);

    const formVals = form.getValues();
    const sanitizedData = {
      external_member_id: formVals.test_claim.external_member_id,

      prescriber_npi: formVals.npi,
      prescriber_phone_number: formVals.provider_phone,
      prescriber_fax_number: formVals.provider_fax,
      prescriber_first_name: formVals.provider_first_name,
      prescriber_last_name: formVals.provider_last_name,
      prescriber_email: formVals.provider_email,
      prescriber_alternate_fax_number: stripPhoneFormatting(formVals.alternate_fax_number),
      urgent: !!formVals.is_urgent,

      patient_first_name: formVals.member_first_name,
      patient_last_name: formVals.member_last_name,
      patient_date_of_birth: formVals.member_date_of_birth,
      ndc: formVals.ndc,
      quantity_prescribed: formVals.quantity_dispensed,
      days_supply: formVals.days_supply,
      directions_for_use: formVals.directions_for_use,
      dispensing_site: formVals.dispensing_site,
      daw: formVals.daw_code,
      diagnosis_codes: formVals.icd_10,

      skip_adjudication: true,
    };

    try {
      const { pa_draft_case_id, unique_code } = await createPACaseDraftAPI(sanitizedData);
      goToCaseDraftSuccess(pa_draft_case_id, unique_code);

    } catch (error) {
      let errorMessage = "Failed to create prior authorization case draft";
      let errorCode: string | undefined;
      let details: Record<string, string> | undefined;

      if (axios.isAxiosError(error)) {
        const responseData = error.response?.data as {
          message?: string;
          error_code?: string;
          details?: Record<string, string>;
        };
        const errMsg = (responseData?.message || "").toLowerCase();
        const duplicateCaseId = responseData?.details?.duplicate_case_id;
        const responseErrorCode = responseData?.error_code;

        if (responseErrorCode === "RENEWAL_CODE_ALREADY_USED") {
          errorMessage = responseData?.message || "This renewal code has already been used";
          errorCode = "RENEWAL_CODE_ALREADY_USED";
        } else if (responseErrorCode === "RENEWAL_CODE_NOT_FOUND") {
          errorMessage = responseData?.message || "Renewal code not found";
          errorCode = "RENEWAL_CODE_NOT_FOUND";
        } else if (errMsg.includes("member not found") || errMsg.includes("invalid member")) {
          errorMessage = "Member not found";
          errorCode = "MEMBER_NOT_FOUND";
        } else if (errMsg.includes("ndc") || errMsg.includes("drug not found")) {
          errorMessage = "Invalid drug NDC";
          errorCode = "INVALID_NDC";
        } else if (errMsg.includes("npi") || errMsg.includes("prescriber") || errMsg.includes("provider")) {
          errorMessage = "Provider NPI is invalid or not found";
          errorCode = "INVALID_PROVIDER";
        } else if (errMsg.includes("not covered") || errMsg.includes("formulary")) {
          errorMessage = "Drug is not covered under member's plan";
          errorCode = "DRUG_NOT_COVERED";
        } else if (errMsg.includes("questionnaire") || errMsg.includes("clinical criteria")) {
          errorMessage = "Required clinical criteria questions are missing or invalid";
          errorCode = "INVALID_QUESTIONNAIRE";
        } else if (errMsg.includes("duplicate") || !!duplicateCaseId) {
          errorMessage = responseData?.message || "A duplicate case is already in progress";
          errorCode = "DUPLICATE_CASE";
        } else if (errMsg.includes("pa tool")) {
          errorMessage = "This client has not been migrated to PA Tool";
          errorCode = "PA_TOOL_NOT_MIGRATED";
        } else if (errMsg.includes("epa configuration") || errMsg.includes("configuration")) {
          errorMessage = "An ePA configuration is not present for this tenant";
          errorCode = "EPA_CONFIG_MISSING";
        } else if (errMsg.includes("icd") || errMsg.includes("diagnosis")) {
          errorMessage = "One or more diagnosis codes are invalid";
          errorCode = "INVALID_ICD10";
        }

        if (responseData?.error_code && !errorCode) {
          errorCode = responseData.error_code;
        }

        if (responseData?.details) {
          details = responseData.details;
        }
      }
      setCreateError({ message: errorMessage, errorCode, details });
      showAlert({ type: "error", style: "banner", message: errorMessage });

      window.scrollTo({ top: 0, behavior: "smooth" });
    }

  }, [showAlert, form]);

  const resetQuestionnaires = () => {
    form.setValue("questionnaires", null);
    form.setValue("questionnaire_responses", null);
    questionnaireForm.reset();
  };

  const goToCase = useCallback((caseId: number) => {
    const formValues = form.getValues();
    const params = {
      case_id: caseId.toString(),
      member_date_of_birth: formValues.member_date_of_birth,
      prescriber_npi: formValues.npi,
      external_member_id: formValues.external_member_id,
      back_url: backUrl,
    };
    const encodedParams = new URLSearchParams();
    for (const key in params) {
      if (params[key]) {
        encodedParams.append(key, params[key]);
      }
    }
    navigate(`${getThemeURL("PA_CASE")}?${encodedParams.toString()}`);
  }, [backUrl, navigate]);

  const goToCaseDraftSuccess = useCallback((caseDraftId: number, accessCode: string) => {
    const params = {
      case_id: caseDraftId.toString(),
      access_code: accessCode,
    };
    const encodedParams = new URLSearchParams();
    for (const key in params) {
      if (params[key]) {
        encodedParams.append(key, params[key]);
      }
    }
    navigate(`${getThemeURL("PA_CREATE_CASE_DRAFT_SUCCESS")}?${encodedParams.toString()}`);
  }, [navigate]);

  return (
    <div className={styles.PACreateFormContainer}>
      <Stack>
        <Stack spacing="small">
          <BackLink linkText="Back" to={backUrl} />
          <Shelf className={styles.PACreateFormSectionTitle}>
            <Title>{isRenewal ? "Renew Prior Authorization Case" : "New Prior Authorization Case"}</Title>
            <Shelf.Spacer />
          </Shelf>
        </Stack>

        {createError && <PACreateErrorDisplay error={createError} />}

        {loading && (
          <Stack className={styles.LoadingContainer} alignment="center">
            <Heading className={styles.LoadingText}>Loading patient data...</Heading>
            <Spinner className={styles.LoadingSpinner} size="large" />
          </Stack>
        )}

        {!loading &&
          <CaseTestClaimForm
            form={form}
            goToNextStep={goToNextStep}
            resetQuestionnaires={resetQuestionnaires}
            isRenewal={isRenewal}
            initialRenewalNpi={initialRenewalNpi}
            initialRenewalCode={initialRenewalCode}
            setIsRenewal={setIsRenewal}
            setPreviousCaseId={setPreviousCaseId}
            previousCaseId={previousCaseId}
            backToEditTrigger={backToEditTrigger}
            backUrl={backUrl}
          />
        }

        {!loading && formProgress >= FormStates.Questionnaire && formProgress !== FormStates.SendDraft &&
          <CaseQuestionnaireForm
            form={form}
            questionnaireForm={questionnaireForm}
            goToNextStep={submitPriorAuthCase} // TODO split into renew and create components?
            prefillQuestions={prefillQuestions}
            showAlternateFaxNumberField={showPrescriberFaxField}
            onBack={goBackToEdit}
          />
        }

        {!loading && formProgress === FormStates.SendDraft &&
          <CaseDraftForm
            form={form}
            questionnaireForm={questionnaireForm}
            goToNextStep={submitPriorAuthCaseDraft}
            onBack={goBackToEdit}
          />
        }

      </Stack>
    </div>
  );
};

export default PACreatePage;
